export type LogicMode = 'recipe' | 'conditional';
export type TargetType = 'passive' | 'active';
export type RollupMode = 'none' | 'sum' | 'weighted_average';
export type QuoteImpact = 'none' | 'unit_price' | 'discount_percent';

export enum Operator {
  ADD = 'add',
  SUBTRACT = 'subtract',
  MULTIPLY = 'multiply',
  DIVIDE = 'divide',
}

export enum ComparisonOperator {
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  EQUAL_TO = 'equal_to',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
}

export enum FormattingOperator {
  CEILING = 'ceiling',
  FLOOR = 'floor',
  ROUND = 'round',
  NONE = 'none',
}

export interface FormulaStep {
  step_id: number;
  type: 'source' | 'operation' | 'formatting';
  value?: string;
  operator?: Operator | FormattingOperator | string;
  operand?: string | number;
  label?: string;
}

export interface FormulaCondition {
  left: string;
  operator: ComparisonOperator;
  right: string | number;
}

export type FormulaActionValueMode = 'percent_of_unit_price';

export interface FormulaAction {
  type: 'assignment';
  value: string | number;
  /** When set, `value` is a 0–100 percent applied to Unit Price. */
  value_mode?: FormulaActionValueMode;
  /** Write this branch result into Unit Price or Discount %. Default is no impact. */
  quote_impact?: QuoteImpact;
}

export interface FormulaMetadata {
  field_label: string;
  field_key: string;
  unit_of_measure: string;
  target_type: TargetType;
  target_field?: string;
  rollup_mode?: RollupMode;
  quote_impact?: QuoteImpact;
}

export interface FormulaJSON {
  metadata: FormulaMetadata;
  logic_mode: LogicMode;
  steps?: FormulaStep[];
  condition?: FormulaCondition;
  true_action?: FormulaAction;
  false_action?: FormulaAction;
}

export interface FormulaVariable {
  id: string;
  label: string;
  type: 'Number' | 'Currency' | 'Percent';
  description?: string;
}

export interface FormulaExecutionResult {
  result: number;
  error?: string;
  warning?: string;
  quote_impact?: QuoteImpact;
}

/**
 * Card #566 (Asim, card #27 review): Formula and Number are reserved custom field TYPE NAMES, not
 * fixed ids. The server reads their ids by name and sends them as `reservedFieldTypeIds` next to
 * `data` on the item `customFieldList` and `formula-variables` responses (card #584); the shared
 * `customfield/list` type rows carry the names too. Until one of those has loaded, the ids the app
 * always used (7 / 8) apply, so a quote opened straight from a link still sums its formula fields
 * and a formula field made early still saves a real type (Rod, 27 Oct).
 */
export const RESERVED_FIELD_TYPE_NAMES = Object.freeze({ FORMULA: 'Formula', NUMBER: 'Number' });

export interface ReservedFieldTypeIds {
  formula: string | null;
  number: string | null;
}

/** The ids before any server answer: the ones every database had when they were hard-coded. */
export const DEFAULT_RESERVED_FIELD_TYPE_IDS = Object.freeze({ formula: '7', number: '8' });

let reservedFieldTypes: { formula: string; number: string } = { ...DEFAULT_RESERVED_FIELD_TYPE_IDS };

function normalizeTypeId(id: unknown): string | null {
  if (id === undefined || id === null) {
    return null;
  }
  const text = String(id).trim();
  return text ? text : null;
}

/**
 * Keeps the ids from a response carrying `reservedFieldTypeIds` (anything else is ignored). A missing,
 * null or empty id never wipes a known one.
 */
export function rememberReservedFieldTypeIds(response: unknown): void {
  const ids = (response as { reservedFieldTypeIds?: Partial<ReservedFieldTypeIds> } | null)?.reservedFieldTypeIds;
  if (!ids || typeof ids !== 'object') {
    return;
  }
  reservedFieldTypes = {
    formula: normalizeTypeId(ids.formula) ?? reservedFieldTypes.formula,
    number: normalizeTypeId(ids.number) ?? reservedFieldTypes.number,
  };
}

/** Back to the ids before any server answer (tests start from here). */
export function resetReservedFieldTypeIds(): void {
  reservedFieldTypes = { ...DEFAULT_RESERVED_FIELD_TYPE_IDS };
}

/** Keeps the ids from the `customfield/list` type rows, found by name. */
export function rememberFieldTypeRows(rows: unknown): void {
  if (!Array.isArray(rows)) {
    return;
  }
  const idNamed = (name: string): string | null =>
    normalizeTypeId(rows.find((row) => String(row?.name ?? '').trim().toLowerCase() === name.toLowerCase())?.id);
  const formula = idNamed(RESERVED_FIELD_TYPE_NAMES.FORMULA);
  const number = idNamed(RESERVED_FIELD_TYPE_NAMES.NUMBER);
  reservedFieldTypes = {
    formula: formula ?? reservedFieldTypes.formula,
    number: number ?? reservedFieldTypes.number,
  };
}

/** The Formula type id: the server's, else 7. Never blank, so a new formula field always saves a type. */
export function formulaFieldTypeId(): string {
  return reservedFieldTypes.formula;
}

/** The Number type id: the server's, else 8. */
export function numberFieldTypeId(): string {
  return reservedFieldTypes.number;
}

/** The type-id fields a custom-field row may carry: flat, or nested under itemsCustomField. */
export interface FieldTypeRow {
  field_type_id?: unknown;
  customerCustomFieldId?: unknown;
  itemsCustomField?: { field_type_id?: unknown } | null;
}

export function isFormulaType(typeId: unknown): boolean {
  return normalizeTypeId(typeId) === reservedFieldTypes.formula;
}

export function isNumberType(typeId: unknown): boolean {
  return normalizeTypeId(typeId) === reservedFieldTypes.number;
}

/**
 * Quote statuses on which a formula may no longer change a line's price or discount. ONE list for
 * the quote screen, the item picker and the runtime (they used to disagree: an approved quote was
 * locked in the picker but still re-priced on the quote screen). develop has no authoritative list,
 * so this is the union of the three. The backend must mirror it.
 */
export const LOCKED_QUOTE_STATUSES: readonly string[] = ['sent', 'accepted', 'won', 'approved', 'declined'];

/**
 * A formula result for DISPLAY: at most 4 decimals, trailing zeros dropped (0.1 + 0.2 shows "0.3",
 * not "0.30000000000000004"). The unrounded number stays in `value` — that is what feeds money.
 */
export function formatFormulaDisplayNumber(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return String(value ?? '');
  }
  return String(parseFloat(n.toFixed(4)));
}

/**
 * Keys a quote line already uses for its own money, quantity and bookkeeping. A custom or formula
 * field with one of these names (any case) must never be written onto the line as a column: a
 * formula named "Price" or "Total" would overwrite the line's money. The builder refuses the names.
 */
export const RESERVED_LINE_KEYS: readonly string[] = [
  // money (create-quote FORMULA_MONEY_KEYS + aliases)
  'perItemPrice', 'Price', 'price', 'discount', 'Discount', 'applyDiscount', 'Total', 'total',
  'total_discount', 'priceAfterDiscount', 'perItemDisc', 'discPrice', 'perItemTax', 'tax_price',
  'priceAftertax', 'totalTaxItem', 'newAddedPrice', 'minPrice', 'totalCost', 'totalCostAll',
  'salesCommission', 'commissionSales', 'profitMargin',
  // quantity
  'Quantity', 'quantity', 'QTY', 'QUANTITY', 'initialQuantity',
  // identity / descriptive columns
  'id', 'itemName', 'Item_name', 'Item name', 'Item_code', 'Item code', 'Item image', 'Item_image',
  'Unit', 'Description', 'description', 'Comments', 'taxes', 'Applicable taxes', 'Tax_exempt',
  'stockStatus', 'inventory', 'itemsComing', 'item_prices',
  // formula state
  'itemsCustomFields', 'formulaResults', 'formulaBase', 'formulaImpactApplied', 'formulaSource',
  'fulldata', 'quote_status', 'isEditable',
  // never object keys
  '__proto__', 'constructor', 'prototype',
];
const RESERVED_LINE_KEYS_LOWER = new Set(RESERVED_LINE_KEYS.map((k) => k.toLowerCase()));

/**
 * The line's own lowercase keys the formula context skips (FormulaContextService.RESERVED_CONTEXT_KEYS).
 * Only the EXACT key is the line's own: a merchant's text field "Category" or "Picture" is a
 * different key and must still reach the quote line (card #553, Adnan on #1562).
 */
const LINE_OWN_KEYS_EXACT: ReadonlySet<string> = new Set([
  'item_type', 'picture', 'category', 'subcategory', 'itemtotal', 'items_custom_field_datas',
]);

/**
 * Names a NEW Number / Formula field may not take, on top of the line keys (any case): the keys
 * above, a field named "Category" never reaching a formula (card #522), "Tax exempt", and words
 * that read as a missing value on screen (card #521). Never used to filter what a quote line
 * copies: that is isReservedLineKey (card #553).
 */
const RESERVED_FIELD_NAMES_EXTRA_LOWER: ReadonlySet<string> = new Set(
  [...LINE_OWN_KEYS_EXACT, 'Tax exempt', 'null', 'undefined', 'NaN'].map((k) => k.toLowerCase()),
);

/** A custom-field name that must never be written onto a quote line as a column (it would overwrite the line's own value). */
export function isReservedLineKey(name: unknown): boolean {
  const trimmed = String(name ?? '').trim();
  return RESERVED_LINE_KEYS_LOWER.has(trimmed.toLowerCase()) || LINE_OWN_KEYS_EXACT.has(trimmed);
}

/** A name the field builders refuse for a new or renamed Number / Formula field (whole name, any case). */
export function isReservedFieldName(name: unknown): boolean {
  return isReservedLineKey(name) || RESERVED_FIELD_NAMES_EXTRA_LOWER.has(String(name ?? '').trim().toLowerCase());
}

/**
 * The reserved names a person would actually type, for the error message (card #522: the old
 * message named three examples, so "Qty" being refused looked arbitrary). Internal keys
 * (perItemDisc, formulaBase…) stay blocked but are not listed. Matching is whole-name, any case.
 */
export const RESERVED_NAMES_SHOWN: readonly string[] = [
  'Price', 'Discount', 'Total', 'Quantity', 'Qty', 'Unit', 'Description', 'Comments', 'Taxes',
  'Applicable taxes', 'Tax exempt', 'Item name', 'Item code', 'Item image', 'Inventory', 'Category',
  'Subcategory', 'Picture', 'ID',
];

/**
 * Card #566 (Asim, card #27 review): the name, unit and run-order rules have ONE copy, in
 * shared-services (field-rules.ts). It rides as `fieldRules` on the `formula-variables` response
 * (card #565); patterns travel as source + flags. Before that response has loaded the screen
 * does not check these three rules itself: the server still refuses on save, and its message is
 * shown translated (ErrorInterceptor).
 */
interface PatternRule {
  source: string;
  flags?: string;
}

interface LoadedFieldRules {
  fieldName: RegExp | null;
  unitSuffix: RegExp | null;
  executionOrder: { min: number; max: number } | null;
}

let loadedFieldRules: LoadedFieldRules = { fieldName: null, unitSuffix: null, executionOrder: null };

function compileRule(rule: unknown): RegExp | null {
  const source = (rule as PatternRule | null)?.source;
  if (typeof source !== 'string' || !source) {
    return null;
  }
  try {
    const flags = (rule as PatternRule).flags;
    return new RegExp(source, typeof flags === 'string' ? flags : '');
  } catch {
    return null;
  }
}

/** Keeps the rules from a response carrying `fieldRules` (anything else is ignored). */
export function rememberFieldRules(response: unknown): void {
  const rules = (response as { fieldRules?: Record<string, unknown> } | null)?.fieldRules;
  if (!rules || typeof rules !== 'object') {
    return;
  }
  const order = rules['executionOrder'] as { min?: unknown; max?: unknown } | undefined;
  const min = Number(order?.min);
  const max = Number(order?.max);
  loadedFieldRules = {
    fieldName: compileRule(rules['fieldNamePattern']),
    unitSuffix: compileRule(rules['unitSuffixPattern']),
    executionOrder: Number.isInteger(min) && Number.isInteger(max) ? { min, max } : null,
  };
}

/** False only when the server's name rule is loaded and the name breaks it. */
export function passesFieldNameRule(name: string): boolean {
  return !loadedFieldRules.fieldName || loadedFieldRules.fieldName.test(name);
}

/** False only when the server's unit rule is loaded and the unit breaks it. */
export function passesUnitSuffixRule(unit: string): boolean {
  return !loadedFieldRules.unitSuffix || loadedFieldRules.unitSuffix.test(unit);
}

/** A whole number, typed as text so junk like "0--." can be shown an error, within the server's range once loaded. */
export function passesExecutionOrderRule(order: unknown): boolean {
  const text = String(order ?? '').trim();
  if (!/^\d+$/.test(text)) {
    return false;
  }
  const range = loadedFieldRules.executionOrder;
  return !range || (Number(text) >= range.min && Number(text) <= range.max);
}

export function isLockedQuoteStatus(status: unknown): boolean {
  return LOCKED_QUOTE_STATUSES.includes(String(status ?? '').trim().toLowerCase());
}

/**
 * Formula and Number come from the shared `customfield/list` (custom_fields table) but are
 * item-only types. Every picker other than the item custom-field screens drops them, by name (the
 * rows carry it) or by the ids the server sent.
 */
export function withoutItemOnlyFieldTypes<T extends { id?: unknown; name?: unknown }>(list: T[] | null | undefined): T[] {
  if (!Array.isArray(list)) {
    return [];
  }
  rememberFieldTypeRows(list);
  const reservedNames = [RESERVED_FIELD_TYPE_NAMES.FORMULA, RESERVED_FIELD_TYPE_NAMES.NUMBER].map((n) => n.toLowerCase());
  return list.filter(
    (type) =>
      !reservedNames.includes(String(type?.name ?? '').trim().toLowerCase()) &&
      !isFormulaType(type?.id) &&
      !isNumberType(type?.id),
  );
}
export const UNIT_PRICE_TOKEN = 'Item.Unit_Price';
export const DISCOUNT_PERCENT_TOKEN = 'Item.Discount_Percent';

export const CORE_FORMULA_VARIABLES: FormulaVariable[] = [
  { id: 'Item.Quantity', label: 'Quantity', type: 'Number', description: 'Line item quantity' },
  { id: 'Item.Total', label: 'Total Item Price', type: 'Currency', description: 'Quantity × Unit Price' },
  { id: 'Item.Unit_Price', label: 'Unit Price', type: 'Currency', description: 'Active price per unit' },
  { id: 'Item.Price_A', label: 'Base Price (Price A)', type: 'Currency', description: 'Standard list price' },
  { id: 'Item.Price_B', label: 'Price Level B', type: 'Currency', description: 'Price tier B' },
  { id: 'Item.Price_C', label: 'Price Level C', type: 'Currency', description: 'Price tier C' },
  { id: 'Item.Price_D', label: 'Price Level D', type: 'Currency', description: 'Price tier D' },
  { id: 'Item.Discount_Percent', label: 'Discount %', type: 'Percent', description: 'Percentage discount' },
];

export function slugifyFieldKey(label: string): string {
  const slug = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug ? `custom_${slug}` : 'custom_field';
}

export function createDefaultFormula(label = 'New Formula', unit = ''): FormulaJSON {
  const fieldKey = slugifyFieldKey(label);
  return {
    metadata: {
      field_label: label,
      field_key: fieldKey,
      unit_of_measure: unit,
      target_type: 'passive',
      target_field: fieldKey,
      rollup_mode: 'none',
      quote_impact: 'none',
    },
    logic_mode: 'recipe',
    steps: [
      {
        step_id: 1,
        type: 'source',
        value: 'Item.Quantity',
        label: 'Start with Quantity',
      },
    ],
  };
}

export function createEmptyConditionalParts(): Pick<
  FormulaJSON,
  'condition' | 'true_action' | 'false_action'
> {
  return {
    condition: {
      left: '',
      operator: ComparisonOperator.GREATER_THAN,
      right: '',
    },
    true_action: { type: 'assignment', value: '' },
    false_action: { type: 'assignment', value: '' },
  };
}

export function createDefaultConditional(label = 'New Formula', unit = ''): FormulaJSON {
  const fieldKey = slugifyFieldKey(label);
  return {
    metadata: {
      field_label: label,
      field_key: fieldKey,
      unit_of_measure: unit,
      target_type: 'passive',
      target_field: fieldKey,
      rollup_mode: 'none',
      quote_impact: 'none',
    },
    logic_mode: 'conditional',
    ...createEmptyConditionalParts(),
  };
}

export function isVariableToken(value: unknown): value is string {
  return typeof value === 'string' && (value.startsWith('Item.') || value.startsWith('Quote.'));
}

export function clampDiscountPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
}

/**
 * Rod, 2026-10-09 (option A): a line's regular discount (picked in the line dialog) and a
 * calculation's discount (quote impact discount_percent) COMPOUND: $100, 50% calc, then 10% more
 * = $45, i.e. 55% off = 1 - (1 - 0.5)(1 - 0.1). Order does not matter; never above 100%.
 */
export function compoundDiscountPercent(regular: number, calculated: number): number {
  const r = clampDiscountPercent(Number(regular)) / 100;
  const c = clampDiscountPercent(Number(calculated)) / 100;
  const combined = (1 - (1 - r) * (1 - c)) * 100;
  // Two decimals, like every discount on screen: 50 + 10 reads 55 (not 55.00000000000001), and a
  // round-trip through a label or a 2-decimal display can never look like a user edit.
  return clampDiscountPercent(Math.round(combined * 100) / 100);
}

export function isPercentOfUnitPriceAction(action?: FormulaAction | null): boolean {
  return action?.value_mode === 'percent_of_unit_price';
}

export function applyDiscountPercentToUnitPrice(percent: number, unitPrice: number): number {
  const price = Number.isFinite(unitPrice) ? unitPrice : 0;
  return price * (clampDiscountPercent(percent) / 100);
}

export function normalizeRollupMode(value: unknown): RollupMode {
  if (value === 'sum' || value === 'weighted_average' || value === 'none') {
    return value;
  }
  return 'none';
}

export function normalizeQuoteImpact(value: unknown): QuoteImpact {
  if (value === 'unit_price' || value === 'discount_percent' || value === 'none') {
    return value;
  }
  return 'none';
}

export function quoteImpactFromMetadata(metadata?: FormulaMetadata | null): QuoteImpact {
  const explicit = normalizeQuoteImpact(metadata?.quote_impact);
  if (explicit !== 'none') {
    return explicit;
  }
  if (metadata?.target_type === 'active') {
    const field = String(metadata.target_field || '');
    if (field === UNIT_PRICE_TOKEN || field === 'Item.Unit_Price') {
      return 'unit_price';
    }
    if (field === DISCOUNT_PERCENT_TOKEN || field === 'Item.Discount_Percent') {
      return 'discount_percent';
    }
  }
  return 'none';
}

export function quoteImpactFromAction(action?: FormulaAction | null): QuoteImpact | undefined {
  if (!action || action.quote_impact == null) {
    return undefined;
  }
  return normalizeQuoteImpact(action.quote_impact);
}

export function resolvedActionQuoteImpact(
  action?: FormulaAction | null,
  formula?: FormulaJSON | null,
): QuoteImpact {
  const fromAction = quoteImpactFromAction(action);
  if (fromAction !== undefined) {
    return fromAction;
  }
  return quoteImpactFromMetadata(formula?.metadata);
}

export function derivedQuoteImpactFromFormula(formula?: FormulaJSON | null): QuoteImpact {
  if (!formula) {
    return 'none';
  }
  if (formula.logic_mode === 'conditional') {
    const trueImpact = quoteImpactFromAction(formula.true_action);
    const falseImpact = quoteImpactFromAction(formula.false_action);
    if (trueImpact && trueImpact !== 'none' && falseImpact && falseImpact !== 'none' && trueImpact !== falseImpact) {
      return 'none';
    }
    if (trueImpact && trueImpact !== 'none') {
      return trueImpact;
    }
    if (falseImpact && falseImpact !== 'none') {
      return falseImpact;
    }
  }
  return quoteImpactFromMetadata(formula.metadata);
}

export function parseFormulaJson(raw: unknown): FormulaJSON | null {
  if (!raw) {
    return null;
  }
  let formula: any = raw;
  if (typeof formula === 'string') {
    try {
      formula = JSON.parse(formula);
    } catch {
      return null;
    }
  }
  if (!formula || typeof formula !== 'object') {
    return null;
  }
  return formula as FormulaJSON;
}

export function itemFormulaJsonFromDatas(datas: any[] | undefined, fieldId: string | number): FormulaJSON | null {
  if (!Array.isArray(datas) || fieldId == null || fieldId === '') {
    return null;
  }
  const row = datas.find((d) =>
    String(d?.customerCustomFieldId || d?.custom_field_id || d?.itemsCustomField?.id || '') === String(fieldId),
  );
  const own = parseFormulaJson(row?.formula_json);
  if (own) {
    return own;
  }
  // Catalog rows have itemId: never fall back to the shared field definition.
  // Quote line snapshots do not; the nested formula is the one applied to this line.
  const catalogItemId = row?.itemId ?? row?.item_id;
  if (catalogItemId != null && catalogItemId !== '') {
    return null;
  }
  return parseFormulaJson(row?.itemsCustomField?.formula_json);
}

export function quoteImpactFromField(field: any): QuoteImpact {
  const formula = parseFormulaJson(field?.formula_json ?? field);
  return derivedQuoteImpactFromFormula(formula);
}

export function targetTypeFromQuoteImpact(impact: QuoteImpact): TargetType {
  return impact === 'none' ? 'passive' : 'active';
}

export function targetFieldFromQuoteImpact(impact: QuoteImpact, fieldKey: string): string {
  if (impact === 'unit_price') {
    return UNIT_PRICE_TOKEN;
  }
  if (impact === 'discount_percent') {
    return DISCOUNT_PERCENT_TOKEN;
  }
  return fieldKey;
}

export function rollupModeFromField(field: any): RollupMode {
  let formula = field?.formula_json;
  if (typeof formula === 'string') {
    try {
      formula = JSON.parse(formula);
    } catch {
      formula = null;
    }
  }
  return normalizeRollupMode(formula?.metadata?.rollup_mode ?? field?.rollup_mode);
}

export interface QuoteFormulaLine {
  itemName: string;
  quantity: number;
  value: number | null;
  /** The engine's error for this line (e.g. "Unknown variable X"); value is null then. */
  error?: string;
}

export interface QuoteFormulaSummary {
  field: any;
  rollup: RollupMode;
  total: number | null;
  lines: QuoteFormulaLine[];
}

export function variableFromCustomField(field: {
  name?: string;
  field_key?: string;
  field_type_id?: string | number;
}): FormulaVariable | null {
  const isFormula = isFormulaType(field?.field_type_id);
  if (!isFormula && !isNumberType(field?.field_type_id)) {
    return null;
  }
  const name = String(field?.name || '').trim();
  if (!name && !field?.field_key) {
    return null;
  }
  const key = field.field_key || slugifyFieldKey(name);
  const attr = key.startsWith('custom_') ? key.slice('custom_'.length) : key;
  const attrId = attr
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  if (!attrId) {
    return null;
  }
  return {
    id: `Item.Custom.${attrId}`,
    label: name || attrId,
    type: 'Number',
    description: isFormula ? `Formula field: ${name}` : `Custom field: ${name}`,
  };
}
